import { Show, createResource, createSignal, type Component } from "solid-js";
import { api } from "../api";

interface Props {
  notFound?: boolean;
}

interface PublicConfig {
  google_auth_configured: boolean;
  demo_mode: boolean;
}

const LoginPage: Component<Props> = (props) => {
  const [config] = createResource(() => api<PublicConfig>("/api/config"));
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  async function demoLogin(): Promise<void> {
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/demo", { method: "POST" });
      window.location.assign("/app");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start the demo.");
      setBusy(false);
    }
  }

  return (
    <main class="home-page">
      <header class="home-header">
        <a class="app-logo" href="/" aria-label="Metripact meet">
          <span class="brand-mark">
            <i />
            <i />
          </span>
          <strong>Metripact meet</strong>
        </a>
        <nav aria-label="Legal information">
          <a href="/privacy">Privacy Notice</a>
          <a href="/terms">Terms</a>
        </nav>
      </header>

      <Show when={props.notFound}>
        <p class="home-alert" role="alert">
          That scheduling page could not be found. You can continue from the
          Metripact meet homepage below.
        </p>
      </Show>

      <section class="home-hero" aria-labelledby="home-title">
        <div class="home-hero-copy">
          <p class="home-kicker">Online meeting scheduling</p>
          <h1 id="home-title">Metripact meet</h1>
          <h2 class="home-headline">Meet without the back-and-forth</h2>
          <p class="home-purpose">
            <strong>Metripact meet</strong> is a scheduling application that lets
            hosts publish their availability and lets invitees book a convenient
            time in their own timezone.
          </p>
          <p>
            When a booking is confirmed, Metripact meet creates a Google Calendar
            event with a Google Meet link and sends the meeting details to the host,
            invitee, and any added guests.
          </p>
        </div>

        <aside class="home-sign-in" aria-labelledby="host-sign-in-title">
          <p class="home-kicker">For meeting hosts</p>
          <h2 id="host-sign-in-title">Manage your scheduling page</h2>
          <p>
            Sign in to configure your working hours, timezone, event types, and
            branding.
          </p>
          <div class="login-actions">
            <Show when={config()?.google_auth_configured}>
              <a class="google-button" href="/api/auth/google/start">
                <span class="google-g">G</span>
                Continue with Google
              </a>
            </Show>
            <Show when={config()?.demo_mode}>
              <button
                class="primary-button"
                type="button"
                disabled={busy()}
                onClick={demoLogin}
              >
                {busy() ? "Opening workspace…" : "Open demo workspace"}
              </button>
            </Show>
          </div>
          <Show when={error()}>
            <p class="form-error" role="alert">
              {error()}
            </p>
          </Show>
          <p class="login-legal">
            By continuing, you agree to our <a href="/terms">Terms</a> and{" "}
            <a href="/privacy">Privacy Notice</a>.
          </p>
        </aside>
      </section>

      <section class="home-section" aria-labelledby="how-it-works">
        <p class="home-kicker">Purpose and features</p>
        <h2 id="how-it-works">How Metripact meet works</h2>
        <div class="home-feature-grid">
          <article>
            <span>1</span>
            <h3>Publish availability</h3>
            <p>
              Hosts choose their timezone and working hours, then share a public
              scheduling link.
            </p>
          </article>
          <article>
            <span>2</span>
            <h3>Find a convenient time</h3>
            <p>
              Invitees see available slots converted to their own timezone while
              existing busy times remain unavailable.
            </p>
          </article>
          <article>
            <span>3</span>
            <h3>Create the meeting</h3>
            <p>
              The confirmed booking becomes a Google Calendar event with attendees
              and a Google Meet conference link.
            </p>
          </article>
        </div>
      </section>

      <section class="google-data-section" aria-labelledby="google-data-title">
        <div>
          <p class="home-kicker">Transparent Google data access</p>
          <h2 id="google-data-title">
            Why Metripact meet requests Google access
          </h2>
          <p>
            Google access is used only to provide sign-in and meeting scheduling
            features requested by the host.
          </p>
        </div>
        <dl>
          <div>
            <dt>Google profile and email</dt>
            <dd>Authenticates the host and identifies their Metripact meet account.</dd>
          </div>
          <div>
            <dt>Calendar and free/busy information</dt>
            <dd>
              Identifies the primary calendar and timezone, checks busy periods, and
              prevents double-booking. Event titles and descriptions are not read
              for availability checks.
            </dd>
          </div>
          <div>
            <dt>Calendar events</dt>
            <dd>
              Creates, updates, and cancels meetings, adds participants, and creates
              the Google Meet conference link.
            </dd>
          </div>
        </dl>
        <p class="data-commitment">
          Metripact meet does not sell Google user data. Calendar connection tokens
          are encrypted. For details about access, use, storage, and service
          providers, read the <a href="/privacy">Privacy Notice</a>.
        </p>
      </section>

      <footer class="home-footer">
        <strong>Metripact meet</strong>
        <span>A scheduling application by Metripact.</span>
        <nav aria-label="Footer links">
          <a href="/privacy">Privacy Notice</a>
          <a href="/terms">Terms</a>
        </nav>
      </footer>
    </main>
  );
};

export default LoginPage;
